import { toast } from 'react-toastify';

import FlashMessage from '~/shared/components/flash-message';
import { isBrowser } from '~/shared/helpers/browser-helpers';
import {
  FetchGraphQLError,
  type GraphQLResponse,
  getLocalizedHttpError,
  parseGraphQLOperation,
  reportRumError,
} from '~/shared/helpers/graphql-error-helpers';
import { processGraphQLResponse } from '~/shared/helpers/process-graphql-response';
import { createClientRequestId } from '~/shared/libs/observability/client-request-id';
import { API_HOST, LOCALES, type Locale } from '~/universal/constants';

const isProduction = process.env.NODE_ENV === 'production';

const shouldShowTechnicalError = !isProduction;

type FetchGraphQLParams<TVariables, TResult, TSelected> = Readonly<{
  operationName?: string;
  query: string;
  variables?: TVariables;
  lng?: Locale;
  signal?: AbortSignal;
  select?: (data: TResult) => TSelected | null | undefined;
  emptyDataMessage?: string;
  showToasts?: boolean;
}>;

export const fetchGraphQL = async <TResult, TVariables = Record<string, unknown>, TSelected = TResult>({
  operationName,
  query,
  variables,
  lng = LOCALES.UA,
  signal,
  select,
  emptyDataMessage = 'GraphQL response has no data',
  showToasts = false,
}: FetchGraphQLParams<TVariables, TResult, TSelected>): Promise<TSelected> => {
  const clientRequestId = isBrowser() ? createClientRequestId() : undefined;
  const { opType, opName } = parseGraphQLOperation(query);
  const resolvedOperationName = operationName || opName;

  const response = await fetch(`${API_HOST}/api/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': lng,
      ...(clientRequestId ? { 'x-client-request-id': clientRequestId } : {}),
    },
    body: JSON.stringify({
      query,
      variables,
      ...(resolvedOperationName ? { operationName: resolvedOperationName } : {}),
    }),
    signal,
  });

  if (!response.ok) {
    reportRumError({
      source: 'fetchGraphQL',
      operationName: resolvedOperationName,
      message: `HTTP ${response.status}`,
      clientRequestId,
      variables,
      extra: {
        opType: opType || '',
        httpStatus: String(response.status),
      },
    });

    const localizedHttpError = await getLocalizedHttpError({
      status: response.status,
    });

    if (showToasts && isBrowser() && shouldShowTechnicalError) {
      toast.error(<FlashMessage title={localizedHttpError.title} message={localizedHttpError.message} />);
    }

    throw new FetchGraphQLError({
      message: localizedHttpError.message,
      title: localizedHttpError.title,
      statusCode: response.status,
      isTechnical: true,
    });
  }

  const result = (await response.json()) as GraphQLResponse<TResult>;

  try {
    const data = await processGraphQLResponse<TResult>({
      payload: result,
      source: 'fetchGraphQL',
      operationName: resolvedOperationName,
      clientRequestId,
      variables,
      opType,
    });

    if (!select) {
      return data as TSelected;
    }

    const selectedData = select(data);

    if (selectedData == null) {
      throw new FetchGraphQLError({
        message: emptyDataMessage,
        isTechnical: true,
      });
    }

    return selectedData;
  } catch (error) {
    if (
      showToasts &&
      isBrowser() &&
      shouldShowTechnicalError &&
      error instanceof FetchGraphQLError &&
      error.title &&
      (shouldShowTechnicalError || !error.isTechnical)
    ) {
      toast.error(<FlashMessage title={error.title} message={error.message} />);
    }

    throw error;
  }
};
